import React from 'react';
import CopyButton from './CopyButton';

const ResultsSection = ({ result }) => {
  if (!result) return null;

  // Helper function to extract footnote number from the footnote object
  const getFootnoteNumber = (footnote) => {
    if (!footnote) return null;
    // The footnote object has the structure {footnote_number: position}
    const footnoteNumber = Object.keys(footnote)[0];
    return footnoteNumber ? parseInt(footnoteNumber) : null;
  };

  return (
    <section className="relative mt-8">
      {/* Background gradient */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute left-1/2 -translate-x-1/2 bottom-0">
          <div className="h-[600px] w-[1400px] bg-gradient-to-r from-indigo-500/10 via-indigo-400/5 to-indigo-300/5 blur-3xl"></div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative">
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg shadow-xl p-6 border border-gray-700">
          <h2 className="text-2xl font-semibold text-white mb-4">Results for {result.filename}</h2>
          
          {/* Citations section */}
          {result.citations && (
            <div className="bg-gray-900/70 rounded-lg p-4">
              <h3 className="text-xl font-semibold text-white mb-4">Generated Citations</h3>

              <div className="space-y-6">
                {['ARTICLE', 'BOOK', 'CASELAW'].map(type => {
                  const typeCitations = result.citations.filter(
                    citation => citation.source_type && citation.source_type.toUpperCase() === type
                  );

                  if (typeCitations.length === 0) return null;

                  // Define colors based on type
                  const typeColors = {
                    'ARTICLE': 'indigo',
                    'BOOK': 'purple',
                    'CASELAW': 'blue'
                  };

                  return (
                    <div key={type} className="bg-gray-900/50 rounded-lg p-4">
                      <h4 className={`text-lg font-semibold text-${typeColors[type]}-300 mb-3`}>
                        {type}
                      </h4>
                      <div className="space-y-3">
                        {typeCitations.map((citation, index) => {
                          const footnoteNumber = getFootnoteNumber(citation.footnote);
                          return (
                            <div
                              key={index}
                              className={`bg-gray-950 rounded-lg p-4 border-l-4 border-${typeColors[type]}-500
                                       transition-all duration-200 hover:shadow-lg hover:scale-[1.01] relative`}
                            >
                              <div className="space-y-4">
                                {/* Original Footnote */}
                                <div>
                                  <div className="flex items-center gap-2 text-sm font-medium text-gray-400 mb-1">
                                    <span>Original Footnote</span>
                                    {footnoteNumber && (
                                      <span className="inline-flex items-center px-2 py-1 bg-gray-700 text-white text-xs font-medium rounded">
                                        Footnote origin: {footnoteNumber}
                                      </span>
                                    )}
                                    {/* Status indicator */}
                                    <span className={`inline-flex items-center px-2 py-1 text-white text-xs font-medium rounded ${
                                      citation.flagged
                                        ? 'bg-red-600'
                                        : 'bg-green-600'
                                    }`}>
                                      {citation.flagged ? 'Manual review required' : 'Reformatted'}
                                    </span>
                                  </div>
                                  <div className="text-gray-300 font-mono text-sm leading-relaxed bg-gray-900/50 p-3 rounded">
                                    {citation.title}
                                  </div>
                                </div>

                                {/* Generated Citation */}
                                <div>
                                  <div className="flex items-center gap-2 text-sm font-medium text-gray-400 mb-1">
                                    <span>Generated Citation</span>
                                    {footnoteNumber && (
                                      <span className="inline-flex items-center px-2 py-1 bg-gray-700 text-white text-xs font-medium rounded">
                                        Footnote origin: {footnoteNumber}
                                      </span>
                                    )}
                                    {/* Status indicator */}
                                    <span className={`inline-flex items-center px-2 py-1 text-white text-xs font-medium rounded ${
                                      citation.flagged
                                        ? 'bg-red-600'
                                        : 'bg-green-600'
                                    }`}>
                                      {citation.flagged ? 'Manual review required' : 'Reformatted'}
                                    </span>
                                  </div>
                                  <div className="text-gray-300 font-mono text-sm leading-relaxed bg-gray-900/50 p-3 rounded relative">
                                    {citation.citation}
                                    <CopyButton text={citation.citation} />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {/* Unsupported / needs-manual-review sources, rendered last in their own section.
                    Catch-all: any UNSUPPORTED-typed source plus any other flagged source not in a
                    supported bucket (and not a supra note), so no real source is silently dropped.
                    All are reinserted as their original text (red) in the output doc. */}
                {(() => {
                  const SUPPORTED_TYPES = ['ARTICLE', 'BOOK', 'CASELAW'];
                  const unsupportedCitations = result.citations.filter(citation => {
                    if (citation.is_supra_note) return false;
                    const type = (citation.source_type || '').toUpperCase();
                    if (SUPPORTED_TYPES.includes(type)) return false; // shown in its own bucket above
                    // UNSUPPORTED type, or any other flagged straggler that didn't land in a bucket.
                    return type === 'UNSUPPORTED' || citation.flagged;
                  });

                  if (unsupportedCitations.length === 0) return null;

                  return (
                    <div key="UNSUPPORTED" className="bg-gray-900/50 rounded-lg p-4 border border-red-900/50">
                      <h4 className="text-lg font-semibold text-red-300 mb-1">
                        UNSUPPORTED / NEEDS MANUAL REVIEW
                      </h4>
                      <p className="text-sm text-gray-400 mb-3">
                        These sources were left untouched in the output document (their original text,
                        highlighted in red) and must be cited manually. They are either of a type this
                        tool does not format (e.g. constitutions, statutes, regulations, treaties,
                        lectures, reports) or could not be confidently classified.
                      </p>
                      <div className="space-y-3">
                        {unsupportedCitations.map((citation, index) => {
                          const footnoteNumber = getFootnoteNumber(citation.footnote);
                          return (
                            <div
                              key={index}
                              className="bg-gray-950 rounded-lg p-4 border-l-4 border-red-500
                                       transition-all duration-200 hover:shadow-lg hover:scale-[1.01] relative"
                            >
                              <div>
                                <div className="flex items-center gap-2 text-sm font-medium text-gray-400 mb-1">
                                  <span>Original Footnote</span>
                                  {footnoteNumber && (
                                    <span className="inline-flex items-center px-2 py-1 bg-gray-700 text-white text-xs font-medium rounded">
                                      Footnote origin: {footnoteNumber}
                                    </span>
                                  )}
                                  <span className="inline-flex items-center px-2 py-1 bg-red-600 text-white text-xs font-medium rounded">
                                    Cite manually
                                  </span>
                                </div>
                                <div className="text-gray-300 font-mono text-sm leading-relaxed bg-gray-900/50 p-3 rounded">
                                  {citation.title}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ResultsSection;
